import { useCallback, useEffect, useState } from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { apiFetch, ApiFetchError } from "../../utils/apiFetch";

/**
 * Kundenlink einer Immobilie auf der Risiko-Detailseite: Link erzeugen,
 * kopieren, widerrufen. Über den Link bearbeitet der Kunde sein Gebäude ohne
 * Anmeldung und ohne KMVS-Navigation; jede Änderung landet direkt in diesem
 * Risiko (`public-form/:token`).
 *
 * Widerrufen fragt inline nach — kein Browser-Dialog (Konvention 09/2026).
 */

type Kundenlink = {
  token: string;
  url: string;
  expiresAt: string;
  submittedAt?: string | null;
};

const useStyles = makeStyles({
  karte: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    marginBottom: "16px",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    backgroundColor: BRAND.bg,
    ...shorthands.border("1px", "dashed", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    color: BRAND.ink,
  },
  titel: { fontWeight: 700 },
  zeile: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" },
  url: {
    flexGrow: 1,
    minWidth: "200px",
    fontFamily: "monospace",
    fontSize: "0.8rem",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "8px",
    paddingRight: "8px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    ...shorthands.borderRadius("4px"),
    overflowWrap: "anywhere",
  },
  knopf: {
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 700,
    color: BRAND.purple,
    backgroundColor: BRAND.white,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("6px"),
    ":disabled": { opacity: 0.5, cursor: "default" },
  },
  leise: { color: BRAND.muted, fontSize: "0.8rem" },
  fehler: { color: "#a4262c", fontSize: "0.82rem" },
});

const datum = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "";

const KundenlinkKarte = ({ riskObjectId }: { riskObjectId: number }) => {
  const styles = useStyles();
  const [link, setLink] = useState<Kundenlink | null>(null);
  const [laden, setLaden] = useState(true);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [kopiert, setKopiert] = useState(false);
  const [widerrufFrage, setWiderrufFrage] = useState(false);

  const lade = useCallback(async () => {
    try {
      const { data } = await apiFetch<Kundenlink | null>(`risk-objects/${riskObjectId}/kundenlink`);
      setLink(data ?? null);
    } catch {
      setLink(null);
    } finally {
      setLaden(false);
    }
  }, [riskObjectId]);

  useEffect(() => {
    void lade();
  }, [lade]);

  const erzeugen = async () => {
    setBusy(true);
    setFehler(null);
    try {
      const { data } = await apiFetch<Kundenlink>(`risk-objects/${riskObjectId}/kundenlink`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      setLink(data);
    } catch (err) {
      setFehler(
        err instanceof ApiFetchError && err.message
          ? err.message
          : "Der Kundenlink konnte nicht erzeugt werden.",
      );
    } finally {
      setBusy(false);
    }
  };

  const widerrufen = async () => {
    setBusy(true);
    setFehler(null);
    try {
      await apiFetch(`risk-objects/${riskObjectId}/kundenlink`, { method: "DELETE" });
      setLink(null);
      setWiderrufFrage(false);
    } catch {
      setFehler("Der Kundenlink konnte nicht widerrufen werden.");
    } finally {
      setBusy(false);
    }
  };

  const kopieren = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setKopiert(true);
      window.setTimeout(() => setKopiert(false), 2000);
    } catch {
      setFehler("Kopieren nicht möglich — bitte den Link markieren und kopieren.");
    }
  };

  if (laden) return null;

  return (
    <div className={styles.karte} data-testid="kundenlink-karte">
      <span className={styles.titel}>Kundenlink</span>
      {!link ? (
        <div className={styles.zeile}>
          <span className={styles.leise}>
            Der Kunde kann die Angaben zu diesem Gebäude über einen Link selbst ergänzen — ohne
            Anmeldung. Jede Änderung landet direkt hier.
          </span>
          <button type="button" className={styles.knopf} onClick={erzeugen} disabled={busy}>
            Kundenlink erzeugen
          </button>
        </div>
      ) : (
        <>
          <div className={styles.zeile}>
            <span className={styles.url}>{link.url}</span>
            <button type="button" className={styles.knopf} onClick={kopieren}>
              {kopiert ? "Kopiert ✓" : "Kopieren"}
            </button>
            {!widerrufFrage ? (
              <button
                type="button"
                className={styles.knopf}
                onClick={() => setWiderrufFrage(true)}
                disabled={busy}
              >
                Widerrufen
              </button>
            ) : (
              <>
                <span className={styles.leise}>Link wirklich widerrufen?</span>
                <button type="button" className={styles.knopf} onClick={widerrufen} disabled={busy}>
                  Ja, widerrufen
                </button>
                <button type="button" className={styles.knopf} onClick={() => setWiderrufFrage(false)}>
                  Abbrechen
                </button>
              </>
            )}
          </div>
          <span className={styles.leise}>
            Gültig bis {datum(link.expiresAt)}
            {link.submittedAt ? ` · zuletzt vom Kunden gespeichert am ${datum(link.submittedAt)}` : ""}
          </span>
        </>
      )}
      {fehler && <span className={styles.fehler}>{fehler}</span>}
    </div>
  );
};

export default KundenlinkKarte;
